'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  findBookableServiceById,
  getAddons,
  getProviders,
  getSuggestedServices,
  resolveProvidersForBooking
} from 'actions/services';
import config from 'config';
import { SERVICES_TABS } from 'constant';
import {
  useGetCategoriesByLocIdQuery,
  useGetServicesByLocIdQuery,
  useGetServicesListQuery
} from 'services/bookingApi';

import { Box, Flex, Stack, Text, useTheme } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  removeAddons,
  removeProvider,
  removeService,
  selectService,
  setPromoCode,
  setPromoData,
  setProvider,
  setServiceActiveStep
} from 'store/reducers/bookingAppointmentSlice';
import { clearSuggestedServices } from 'store/reducers/servicesSlice';
import { setRedirectFlow } from 'store/reducers/userSlice';

import { moveConsultationToFirst } from 'utils';
import { ProviderStepDecision } from 'utils/helpers/providerStep';
import {
  getVisibleSuggestedServices,
  shouldShowSuggestedServicesStep,
  SUGGESTED_SERVICES_STEP_DESCRIPTION,
  SUGGESTED_SERVICES_STEP_TITLE
} from 'utils/helpers/suggestedServices';

import {
  BookServiceSteps,
  CategoryInterface,
  CombinedServiceInterface,
  LocationFlowType,
  ProviderInterface,
  ServiceInterface,
  ServiceType,
  SpecialServicesTabsIds,
  SuggestedService
} from 'types';

import { ContentLoader } from 'components';
import { Button, Divider, Loader, MultiStepHeader } from 'ui';
import {
  AddonList,
  ProvidersList,
  ServiceList,
  ServiceMobileStepper,
  ServiceStepper,
  SuggestedServicesList
} from './components';

const { Review } = SpecialServicesTabsIds;

const {
  booking: {
    defaultProvider,
    service: {
      selectService: {
        selectServiceStep,
        enhanceServiceStep,
        selectProviderStep,
        suggestedServicesStep
      }
    }
  }
} = config;

const defaultSuggestedServicesStep = {
  paddings: { base: 0, md: 0, lg: 0 }
};

interface ServiceStepProps {
  onBack: () => void;
  onNext: () => void;
  onScrollToTop: () => void;
  updateRoute: (id: number) => void;
  isSingleLocation?: boolean;
}

const ServiceStep = ({
  onBack,
  onNext,
  onScrollToTop,
  updateRoute,
  isSingleLocation = false
}: ServiceStepProps) => {
  const dispatch = useAppDispatch();
  const { user, selectedLocation } = useAppSelector((state) => state.user);
  const {
    addons,
    isLoading: isServiceStateLoading,
    isServicesLoading,
    isAddonsLoading,
    isProvidersLoading,
    isSuggestedServicesLoading,
    suggestedServices = []
  } = useAppSelector((state) => state.services);
  const {
    serviceActiveStep,
    selectedServices,
    selectedProviders,
    selectedAddons,
    moveApptId
  } = useAppSelector((state) => state.bookingAppointment);
  const {
    colors: {
      palette: {
        booking: { stepService: stepServices }
      }
    }
  } = useTheme();

  const initialProvidersArray = Object.values(selectedProviders);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [playAddingAnimation, setPlayAddingAnimation] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryInterface | null>(null);

  const setActiveStep = useCallback(
    (num: BookServiceSteps) => dispatch(setServiceActiveStep(num)),
    [dispatch]
  );

  const locationId = selectedLocation?.id ?? user?.home_store_id ?? 0;
  const isWaitingForLocation = locationId === 0;
  const {
    data: currentLocationCategories = [],
    isLoading: isCategoriesLoading,
    isFetching: isCategoriesFetching,
    isUninitialized: isCategoriesUninitialized,
    isError: isCategoriesError
  } = useGetCategoriesByLocIdQuery(locationId, { skip: isWaitingForLocation });

  /** Skip + empty [] looks like "no categories" for one frame after refresh — treat as loading. */
  const isCategoriesStillResolving =
    !isWaitingForLocation &&
    (isCategoriesLoading ||
      isCategoriesUninitialized ||
      (currentLocationCategories.length === 0 &&
        isCategoriesFetching &&
        !isCategoriesError));

  const updatedCategories = moveConsultationToFirst(currentLocationCategories);
  const isCategoriesExist = currentLocationCategories.length > 0;
  const initialCategoryId = currentLocationCategories[0]?.id;
  const selectedCategoryId = selectedCategory?.id ?? initialCategoryId;

  const {
    data: servicesByCategory = [],
    isLoading: isServicesByCategoryLoading
  } = useGetServicesByLocIdQuery(
    { categoryId: selectedCategoryId, locationId },
    {
      skip: !initialCategoryId
    }
  );

  const { data: popularService = [], isLoading: isPopularServiceLoading } =
    useGetServicesListQuery(
      { locationId, type: ServiceType.Popular },
      { skip: locationId === 0 }
    );
  const { data: recentService = [], isLoading: isRecentServiceLoading } =
    useGetServicesListQuery(
      { locationId, type: ServiceType.Recent },
      { skip: locationId === 0 }
    );
  const { data: favoritesService = [], isLoading: isFavoritesServiceLoading } =
    useGetServicesListQuery(
      { locationId, type: ServiceType.Interested },
      { skip: locationId === 0 }
    );

  const isSuggestedServicesStep =
    serviceActiveStep === BookServiceSteps.SuggestedServices;

  const isLoading =
    isServiceStateLoading ||
    isCategoriesLoading ||
    isServicesByCategoryLoading ||
    isPopularServiceLoading ||
    isRecentServiceLoading ||
    isFavoritesServiceLoading ||
    (isSuggestedServicesStep && isSuggestedServicesLoading);

  const specialServicesIds = Object.values(SpecialServicesTabsIds);
  const groupedSpecialServices: Record<number, ServiceInterface[]> = {
    [SpecialServicesTabsIds.Recent]: recentService || [],
    [SpecialServicesTabsIds.Favorites]: favoritesService || [],
    [SpecialServicesTabsIds.Popular]: popularService || []
  };
  const formattedSpecialServices = Object.entries(groupedSpecialServices);
  const specialCategories = formattedSpecialServices
    .filter(([, items]) => items.length > 0)
    .map(([catId]) => SERVICES_TABS[+catId]);
  const allCategories = [...specialCategories, ...updatedCategories];
  const initialCategory = allCategories[0];
  const isLoadingInitialCategory = !selectedCategory && !!initialCategory;

  const contentLoaderShowsSpinner =
    isLoading ||
    isLoadingInitialCategory ||
    isWaitingForLocation ||
    isCategoriesStillResolving;

  const isSpecialServicesSelected =
    specialServicesIds.includes(selectedCategoryId);

  const currentServices = isSpecialServicesSelected
    ? groupedSpecialServices[selectedCategoryId]
    : servicesByCategory;

  const selectedServicesLength = selectedServices.length;
  const selectedProvidersLength = initialProvidersArray.length;
  const isSelectedServicesExist = selectedServicesLength > 0;
  const serviceIds = (selectedServices ?? [])?.map((service) => service?.id);

  const isAddonsSelected = Object.values(selectedAddons).flat(2)?.length > 0;
  const isReviewTab = selectedCategoryId === Review;
  const isServiceStep = serviceActiveStep === BookServiceSteps.Services;
  const isAddonsStep = serviceActiveStep === BookServiceSteps.Addons;
  const isProviderStep = serviceActiveStep === BookServiceSteps.Providers;
  const serviceTitleObject = isServiceStep
    ? selectServiceStep
    : enhanceServiceStep;
  const suggestedServicesStepConfig = {
    ...defaultSuggestedServicesStep,
    ...suggestedServicesStep
  };
  const visibleSuggestions = getVisibleSuggestedServices(suggestedServices);
  const hasSelectedSuggestion = visibleSuggestions.some((item) =>
    serviceIds.includes(item.item_id)
  );
  const hasSuggestedServicesStep = shouldShowSuggestedServicesStep(
    selectedServices,
    suggestedServices
  );
  const stepHeaderContent = (() => {
    if (isSuggestedServicesStep) {
      return {
        title: SUGGESTED_SERVICES_STEP_TITLE,
        description: SUGGESTED_SERVICES_STEP_DESCRIPTION,
        paddings: suggestedServicesStepConfig.paddings
      };
    }

    if (isProviderStep) {
      return selectProviderStep;
    }

    return serviceTitleObject;
  })();
  const { title, description, paddings } = stepHeaderContent;

  const renderedServices = isReviewTab ? selectedServices : currentServices;
  const addonsByLocation = addons[locationId];
  const selectedServicesAddons = serviceIds.filter(
    (id) => !!addonsByLocation?.[id]
  );
  const isAddonsExist = selectedServicesAddons
    .map((item) => addonsByLocation[item].length)
    .some(Boolean);

  const fetchAddons = () => {
    const toFetch = serviceIds.filter(
      (id) => !selectedServicesAddons.includes(id)
    );
    return dispatch(getAddons(locationId, toFetch));
  };

  const getSelectedAddonIds = () =>
    Object.values(selectedAddons)
      .flat(2)
      .map((addon) => addon.id);

  const applyProviderStepDecision = (decision: ProviderStepDecision) => {
    if (decision.action === 'show') {
      setActiveStep(BookServiceSteps.Providers);
      return;
    }

    if (decision.action === 'autoSelect') {
      decision.selections.forEach(({ entityId, provider }) => {
        dispatch(
          setProvider({
            service: entityId,
            provider
          })
        );
      });
    }

    onNext();
  };

  const resolveAndApplyProviders = async () => {
    const decision = await dispatch(
      resolveProvidersForBooking(locationId, serviceIds, getSelectedAddonIds())
    );
    applyProviderStepDecision(decision);
  };

  const proceedToSuggestedServicesOrProviders = async () => {
    const suggestions =
      (await dispatch(getSuggestedServices(locationId, serviceIds))) ?? [];

    if (shouldShowSuggestedServicesStep(selectedServices, suggestions)) {
      setActiveStep(BookServiceSteps.SuggestedServices);
    } else {
      await resolveAndApplyProviders();
    }
    onScrollToTop();
  };

  const completeSuggestedServicesStep = async () => {
    await resolveAndApplyProviders();
    onScrollToTop();
  };

  const handleNextStep = async () => {
    let nextStep = serviceActiveStep;

    switch (serviceActiveStep) {
      case 1:
        const addonsData = await fetchAddons();
        if (addonsData?.length || isAddonsExist) {
          nextStep = BookServiceSteps.Addons;
        } else {
          await proceedToSuggestedServicesOrProviders();
          return;
        }
        break;
      case 2:
        await proceedToSuggestedServicesOrProviders();
        return;
      default:
        nextStep = BookServiceSteps.Services;
        break;
    }
    setActiveStep(nextStep);
  };

  const handleBack = () => {
    switch (serviceActiveStep) {
      case BookServiceSteps.Services:
        onBack();
        break;
      case BookServiceSteps.Addons:
        setActiveStep(BookServiceSteps.Services);
        break;
      case BookServiceSteps.SuggestedServices:
        if (isAddonsExist) {
          setActiveStep(BookServiceSteps.Addons);
        } else {
          setActiveStep(BookServiceSteps.Services);
        }
        break;
      case BookServiceSteps.Providers:
        if (hasSuggestedServicesStep) {
          setActiveStep(BookServiceSteps.SuggestedServices);
        } else if (isAddonsExist) {
          setActiveStep(BookServiceSteps.Addons);
        } else {
          setActiveStep(BookServiceSteps.Services);
        }
        break;
      default:
        onBack();
        break;
    }

    onScrollToTop();
  };

  const handleNext = () => {
    if (isSuggestedServicesStep) {
      completeSuggestedServicesStep();
    } else if (isProviderStep) {
      onNext();
      onScrollToTop();
    } else {
      handleNextStep().then(onScrollToTop);
    }
  };

  const handleRemoveService = (id: number) => {
    updateRoute(id);
    dispatch(removeService(id));
    dispatch(removeProvider({ service: id }));
    dispatch(removeAddons({ service: id }));

    if (
      (selectedServicesLength === 1 && isReviewTab) ||
      selectedProvidersLength === 1
    ) {
      setActiveStep(BookServiceSteps.Services);
      dispatch(setRedirectFlow(null));
    }
  };

  const handleAddServices = (serviceItem: ServiceInterface) => {
    updateRoute(serviceItem.id);

    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }
    setPlayAddingAnimation(true);
    timeoutRef.current = setTimeout(() => {
      setPlayAddingAnimation(false);
    }, 1000);
    dispatch(setRedirectFlow(LocationFlowType.BOOK));
    dispatch(selectService(serviceItem));
    dispatch(
      setProvider({
        service: serviceItem.id,
        provider: defaultProvider
      })
    );
  };

  const handleToggleSuggestion = async (suggestion: SuggestedService) => {
    if (suggestion.kind === 'P') {
      return;
    }

    const isSelected = selectedServices.some(
      (svc) => svc.id === suggestion.item_id
    );

    if (isSelected) {
      handleRemoveService(suggestion.item_id);
      return;
    }

    const service = await dispatch(
      findBookableServiceById(locationId, suggestion.item_id)
    );

    if (service) {
      handleAddServices(service);
      await dispatch(getProviders(locationId, service.id));
    }
  };

  const handleClickService = (item: ServiceInterface) => {
    const isItemExist = selectedServices.some((value) => value.id === item.id);
    if (isItemExist) {
      handleRemoveService(item.id);
    } else {
      handleAddServices(item);
    }
  };

  const handleToggleProvider = (
    serviceId: number,
    provider: ProviderInterface
  ) => {
    if (selectedProviders[serviceId]?.id === provider.id) {
      dispatch(setProvider({ service: serviceId, provider: defaultProvider }));
    } else {
      dispatch(setProvider({ service: serviceId, provider }));
    }
  };

  const renderContent = () => {
    switch (serviceActiveStep) {
      case BookServiceSteps.Addons:
        return <AddonList locationId={locationId} />;
      case BookServiceSteps.Providers:
        return (
          <ProvidersList
            selectedProviders={selectedProviders}
            onToggleProvider={handleToggleProvider}
            onRemoveService={handleRemoveService}
            locationId={locationId}
          />
        );
      case BookServiceSteps.SuggestedServices:
        return (
          <SuggestedServicesList
            selectedServices={selectedServices}
            selectedServiceIds={serviceIds}
            onToggleSuggestion={handleToggleSuggestion}
          />
        );
      default:
        return (
          <ServiceList
            services={renderedServices as CombinedServiceInterface[]}
            selectedItems={selectedServices}
            onServiceClick={handleClickService}
            isReviewTab={isReviewTab}
            isLoading={isServicesLoading}
          />
        );
    }
  };

  useEffect(() => {
    dispatch(setPromoCode(''));
    dispatch(setPromoData(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedServiceIdsKey = serviceIds.join(',');

  useEffect(() => {
    if (!isServiceStep && !isAddonsStep) {
      return;
    }
    dispatch(clearSuggestedServices());
  }, [dispatch, isAddonsStep, isServiceStep, selectedServiceIdsKey]);

  useEffect(() => {
    if (isLoadingInitialCategory && initialCategory !== selectedCategory) {
      setSelectedCategory(initialCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCategory, isLoadingInitialCategory, selectedCategory]);

  const nextButtonLabel = (() => {
    if (isSuggestedServicesStep) {
      return hasSelectedSuggestion ? 'Continue' : 'Skip';
    }

    if (isAddonsStep && !isAddonsSelected) {
      return 'Skip';
    }

    return 'Next';
  })();

  return (
    <Stack gap='10px' w='100%' mx='auto' position='relative'>
      <MultiStepHeader
        title={title}
        subTitle={description}
        headingColor={stepServices.titleColor}
        descriptionColor={stepServices.descriptionColor}
        mt={['30px', '40px', '50px', '60px']}
        mb={['10px', '15px', '20px']}
        descriptionPX={paddings}
      />
      <Divider
        display={{ base: 'none', md: 'block' }}
        h='1px'
        bg={stepServices.dividerColor}
        border='none'
        my='10px'
      />

      {isServiceStep && selectedCategory && (
        <Box
          display={{ base: 'block', md: 'none' }}
          position='sticky'
          top='0'
          mx='-15px'
          mb='5px'
          w='calc(100% + 30px)'
          zIndex={10}
        >
          <ServiceMobileStepper
            categories={allCategories}
            specialCategories={specialCategories}
            currentCategory={selectedCategory}
            onChangeCategory={setSelectedCategory}
            showReviewTab={isServicesLoading}
          />
        </Box>
      )}
      {isLoading || !isCategoriesExist || isLoadingInitialCategory ? (
        <ContentLoader
          isLoading={contentLoaderShowsSpinner}
          label='No categories found'
          isRedVariant
        />
      ) : (
        <>
          <Flex
            flexDirection={{ base: 'column', md: 'row' }}
            gap='5px'
            w='100%'
          >
            {isServiceStep && selectedCategory && (
              <Box
                w='100%'
                display={{ base: 'none', md: 'block' }}
                maxW={{ base: 'none', md: '235px' }}
              >
                <ServiceStepper
                  categories={allCategories}
                  specialCategories={specialCategories}
                  currentCategory={selectedCategory}
                  onChangeCategory={setSelectedCategory}
                  showSummaryTab={isSelectedServicesExist}
                  playAddingAnimation={playAddingAnimation}
                />
              </Box>
            )}
            <Box w='100%'>{renderContent()}</Box>
          </Flex>
          <Divider
            top='0'
            h='1px'
            bg={stepServices.dividerColor}
            mt='10px'
            mb={{ base: '18px', md: '30px' }}
            border='none'
          />
          {isServiceStep && (
            <Text
              display='inline-block'
              mx='auto'
              fontSize='12px'
              textTransform='uppercase'
              color={stepServices.helperTextColor}
              textAlign='center'
              maxW={{ base: '360px', sm: '900px' }}
              lineHeight='200%'
            >
              {selectServiceStep.helperText}
            </Text>
          )}
        </>
      )}
      <Flex
        direction={{ base: 'column-reverse', md: 'row' }}
        mt={{ base: '15px', sm: '15px', md: '20px' }}
        justifyContent='center'
        alignItems='center'
        gap='20px'
        w='100%'
      >
        {(!isSingleLocation || !isServiceStep) && (
          <Button
            variant='outline'
            w='150px'
            onClick={handleBack}
            display={moveApptId ? 'none' : 'block'}
          >
            Back
          </Button>
        )}

        <Button
          w='150px'
          isDisabled={!isSelectedServicesExist || isLoading}
          onClick={handleNext}
        >
          {nextButtonLabel}
        </Button>
      </Flex>
      {(isAddonsLoading ||
        isProvidersLoading ||
        isSuggestedServicesLoading) && (
        <Loader fixed bg={stepServices.loaderBg} />
      )}
    </Stack>
  );
};

export default ServiceStep;
